/* Copyright (c) 2022 MOCA Systems, Inc.  All rights reserved. */

import { IProjectCatalogData } from '../interfaces/IProjectCatalog';
import { ProjectCatalogType } from '../interfaces/ProjectCatalogType';
import { masterCatalog } from '../models/MasterCatalog';
import { fancyFirebaseSchema } from './FancyFirebaseSchema';
import { fancyConfig } from '../FancyConfig';

import { FancyDatabase, IRefLike } from '@touchplan/common';

import { IFancyFirebaseStateCatalogGetter } from '../interfaces/IFancyFirebaseStateCatalogGetter';
import { FancyRef } from '@touchplan/common/lib/FancyRef';

export enum SupportedErrors {
	init = 'fancyFirebaseReferencer.init() must be called before use',
	undefinedPath = 'path is not defined',
	projectIdMissing = 'a projectId is required unless the data is stored on master',
	catalogNotLoaded = 'a projectCatalog has not been loaded before accessing',
	rootWithoutType = 'a root ref is requested without specifying a CatalogType',
}

export class FancyFirebaseReferencerError extends Error {
	public extra: object;
	public constructor(message: string, extra: object) {
		super(message);
		this.extra = extra;
	}
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export declare type DatabaseGetterCallback = (dbUrl?: string) => any;

export class FancyFirebaseReferencer<DbRef extends IRefLike = IRefLike> {
	protected firebaseDatabaseGetter!: DatabaseGetterCallback;
	private _firebaseState!: IFancyFirebaseStateCatalogGetter;
	protected _initialized: boolean = false;

	public fancyDb!: FancyDatabase<DbRef>;

	/** call to pass in initialization data. The service will not work until it's initialized */
	public init(firebaseState: IFancyFirebaseStateCatalogGetter, fancyDb: FancyDatabase<DbRef>): void {
		this._firebaseState = firebaseState;
		this.fancyDb = fancyDb;
		this._initialized = true;
	}

	/**
	 * Returns a `Reference` representing the location on the database based on catalog data.
	 * @param path A rootless path to the data trying to be read ('projects', not 'Root/projects')
	 * @param projectId Optional overall, required when reading projectData or derivedData style nodes
	 * @param projectCatalogTypeOverride A mechanism to disable the "magic" and specifically demand a specific catalog type
	 */
	public getLegacyRef<SdkRef extends DbRef>(
		path: string,
		projectId?: string,
		projectCatalogTypeOverride?: ProjectCatalogType
	): SdkRef {
		const fancyRef = this.getRef(path, projectId, projectCatalogTypeOverride);
		return this.fancyDb.toFirebaseRef(fancyRef) as SdkRef;
	}

	/**
	 * Returns a `FancyRef` representing the location on the database based on catalog data.
	 * @param path A rootless path to the data trying to be read ('projects', not 'Root/projects')
	 * @param projectId Optional overall, required when reading projectData or derivedData style nodes
	 * @param projectCatalogTypeOverride A mechanism to disable the "magic" and specifically demand a specific catalog type
	 */
	public getRef(path: string, projectId?: string, projectCatalogTypeOverride?: ProjectCatalogType): FancyRef {
		if (!this._initialized) {
			throw new Error(SupportedErrors.init);
		}
		if (path === null || path === undefined) {
			throw new Error(SupportedErrors.undefinedPath);
		}

		// accessing the root node directly will require specifying the type
		// didn't really want to litter in the schema
		if (path === '' && !projectCatalogTypeOverride) {
			throw new Error(SupportedErrors.rootWithoutType);
		}

		const pathSegments = this.cleanPathSegmentArray(path);

		const schemaData =
			path !== '' ? fancyFirebaseSchema.getSchema(pathSegments) : { projectCatalogType: null };
		const projectCatalogType = projectCatalogTypeOverride || schemaData.projectCatalogType;

		const masterCatalogInstance = masterCatalog.catalog();

		if (!projectId && !projectCatalogTypeOverride && projectCatalogType !== ProjectCatalogType.Master) {
			throw new Error(SupportedErrors.projectIdMissing);
		}

		//special handling for master, could also consider moving this master handling into state
		let projectCatalog: IProjectCatalogData | undefined;
		if (projectCatalogType === ProjectCatalogType.Master) {
			projectCatalog = masterCatalogInstance;
		} else {
			projectCatalog = this._firebaseState.getProjectCatalogSync(
				projectId as string,
				projectCatalogType as ProjectCatalogType
			);
		}

		if (!projectCatalog) {
			// Saving this to debug "catalog not loaded errors"
			const extraData = {
				projectCatalog: projectCatalog || null,
				path,
				projectId: projectId || null,
				projectCatalogType: projectCatalogType || null,
				projectCatalogTypeOverride: projectCatalogTypeOverride || null,
				schemaData: schemaData || null,
				partitioningEnabled: fancyConfig.enablePartitioning,
				fancyConfigFirebaseRefUrl: fancyConfig.firebaseRefUrl,
			};
			// console.log('arrrrrrr');
			// console.log(extraData);
			throw new FancyFirebaseReferencerError(SupportedErrors.catalogNotLoaded, extraData);
		}

		const rootIncludedPath: string = this.makeFinalPath(pathSegments);

		//some pretty fantastic garbage here
		//cannot specify the database explictly for the master database as firebase interprets that as creating two of the same instance
		//so explicitly catch that one and sub for undefined
		const bsDatabaseUrl: string | undefined =
			projectCatalog.databaseURL !== masterCatalogInstance.databaseURL
				? projectCatalog.databaseURL
				: undefined;

		return this.fancyDb.getRef(rootIncludedPath, bsDatabaseUrl);
	}

	/**
	 * Convert a reference back into a string path. Optionally including the refUrl (firebaseGetters does not include this)
	 * Opting for this instead of directly exposing a path getter to discourge path use without a damn good reason.
	 * Only known good reason at the moment is cloud storage
	 * TODO - add unit tests in https://mocasystems.atlassian.net/browse/TP-2741
	 */
	public refToPath(ref: IRefLike, includeRefUrl: boolean = false): string {
		const pathArray: string[] = [];
		if (ref.key) {
			pathArray.unshift(ref.key);
		}

		while (ref.parent?.key) {
			pathArray.unshift(ref.parent.key);
			ref = ref.parent;
		}

		if (!includeRefUrl) {
			pathArray.splice(0, 1);
		}

		//start with a slash for consistency with older stuff (firebase no care)
		return '/' + pathArray.join('/');
	}

	/** Don't use aside from code that needs to straddle functions and frontend. And only specific cases of that */
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	public getFirebaseDatabase(dbUrl?: string): any {
		return this.firebaseDatabaseGetter(dbUrl);
		// return firebase.app().database(dbUrl);
	}

	//break this out so that masterFancyFirebaseReferencer can replace it
	protected getRefUrl(): string {
		return fancyConfig.firebaseRefUrl;
	}

	//clear out extraneous slashes for more consistency, mostly leading and trailing. Will also technically catch and fix double slashes
	protected cleanPathSegmentArray(path: string): string[] {
		return path.split('/').filter((s) => s.length > 0);
	}

	protected makeFinalPath(pathSegments: string[]): string {
		return [...this.cleanPathSegmentArray(this.getRefUrl()), ...pathSegments].join('/');
	}
}
// BEGIN PLACEHOLDER REPLACE AS PART OF TP-6697
// this will be the wrong type and it's probably not fixable while ref's are still part of this file
// this "should" be fine after switching entirely to fancyRef, but it screws up the planned
// refactor ordering, compat stuff and upgrading can't be done until this is done
export const fancyFirebaseReferencer: FancyFirebaseReferencer = new FancyFirebaseReferencer();
// END PLACEHOLDER REPLACE AS PART OF TP-6697
